import { getMarkerJitter, getMarkerUnitNoise } from "./markerStroke";

const ARROW_BEND_MAX = 2.8;
const ARROW_BEND_RATIO = 0.014;
const CORNER_CONTROL_BASE = 0.55;
const CORNER_RADIUS_MAX = 28;
const CORNER_RADIUS_MIN = 8;
const CORNER_RADIUS_RATIO = 0.16;
const ELLIPSE_DISTORTION_MAX = 3.4;
const ELLIPSE_DISTORTION_RATIO = 0.035;
const ELLIPSE_FREQUENCIES = Object.freeze([2, 3, 5]);
const ELLIPSE_SEGMENTS = 24;
const ELLIPSE_WEIGHTS = Object.freeze([0.52, 0.31, 0.17]);
const FRAME_DRIFT_SCALE = 2;
const WING_DRIFT_SCALE = 1.8;

const getRoughFrame = ({ frame, passOffset, seed }) => {
  const getDrift = (index) =>
    getMarkerJitter({ index: index + passOffset, scale: FRAME_DRIFT_SCALE, seed });
  const x = frame.x + getDrift(1);
  const y = frame.y + getDrift(2);

  return {
    bottom: y + frame.height + getDrift(3),
    right: x + frame.width + getDrift(4),
    x,
    y,
  };
};

const getCornerRadius = ({ frame, index, passOffset, seed }) => {
  const minimumDimension = Math.min(frame.width, frame.height);
  const radius = Math.min(
    CORNER_RADIUS_MAX,
    Math.max(CORNER_RADIUS_MIN, minimumDimension * CORNER_RADIUS_RATIO),
    minimumDimension / 2
  );
  const variation = getMarkerJitter({
    index: 20 + index + passOffset,
    scale: 0.2,
    seed,
  });
  return radius * (1 + variation);
};

const createCorner = ({ controlFactor, incoming, outgoing, radius, vertex }) => ({
  controlOne: {
    x: vertex.x - incoming.x * radius * (1 - controlFactor),
    y: vertex.y - incoming.y * radius * (1 - controlFactor),
  },
  controlTwo: {
    x: vertex.x + outgoing.x * radius * (1 - controlFactor),
    y: vertex.y + outgoing.y * radius * (1 - controlFactor),
  },
  end: { x: vertex.x + outgoing.x * radius, y: vertex.y + outgoing.y * radius },
  start: { x: vertex.x - incoming.x * radius, y: vertex.y - incoming.y * radius },
});

const getCornerDefinitions = ({ bottom, right, x, y }) => [
  { incoming: { x: 1, y: 0 }, outgoing: { x: 0, y: 1 }, vertex: { x: right, y } },
  {
    incoming: { x: 0, y: 1 },
    outgoing: { x: -1, y: 0 },
    vertex: { x: right, y: bottom },
  },
  {
    incoming: { x: -1, y: 0 },
    outgoing: { x: 0, y: -1 },
    vertex: { x, y: bottom },
  },
  { incoming: { x: 0, y: -1 }, outgoing: { x: 1, y: 0 }, vertex: { x, y } },
];

const createRoughCorner = ({ definition, frame, index, passOffset, seed }) => {
  const radius = getCornerRadius({ frame, index, passOffset, seed });
  const variation = getMarkerJitter({
    index: 30 + index + passOffset,
    scale: 0.12,
    seed,
  });
  return createCorner({
    ...definition,
    controlFactor: CORNER_CONTROL_BASE + variation,
    radius,
  });
};

/** @returns {Array<object>} deterministic rounded-corner geometry */
export const getRoughRectangleCorners = ({ frame, passOffset, seed }) => {
  const roughFrame = getRoughFrame({ frame, passOffset, seed });
  return getCornerDefinitions(roughFrame).map((definition, index) =>
    createRoughCorner({ definition, frame, index, passOffset, seed })
  );
};

const getDiamondBaseVertices = (frame) => {
  const centerX = frame.x + frame.width / 2;
  const centerY = frame.y + frame.height / 2;
  return [
    { x: centerX, y: frame.y },
    { x: frame.x + frame.width, y: centerY },
    { x: centerX, y: frame.y + frame.height },
    { x: frame.x, y: centerY },
  ];
};

const getRoughVertex = ({ index, passOffset, seed, vertex }) => ({
  x:
    vertex.x +
    getMarkerJitter({
      index: 60 + index + passOffset,
      scale: FRAME_DRIFT_SCALE,
      seed,
    }),
  y:
    vertex.y +
    getMarkerJitter({
      index: 70 + index + passOffset,
      scale: FRAME_DRIFT_SCALE,
      seed,
    }),
});

/** @returns {Array<{x: number, y: number}>} deterministic diamond vertices */
export const getRoughDiamondVertices = ({ frame, passOffset, seed }) =>
  getDiamondBaseVertices(frame).map((vertex, index) =>
    getRoughVertex({ index, passOffset, seed, vertex })
  );

const getEllipseDistortion = ({ angle, phases }) =>
  phases.reduce(
    (sum, phase, index) =>
      sum +
      ELLIPSE_WEIGHTS[index] *
        Math.sin(angle * ELLIPSE_FREQUENCIES[index] + phase),
    0
  );

const createEllipsePoint = ({ amplitude, angle, center, phases, radius }) => {
  const distortion = getEllipseDistortion({ angle, phases });
  return {
    x: center.x + (radius.x + amplitude * distortion) * Math.cos(angle),
    y: center.y + (radius.y + amplitude * distortion) * Math.sin(angle),
  };
};

/** @returns {Array<{x: number, y: number}>} smooth asymmetric ellipse points */
export const getRoughEllipsePoints = ({ frame, passOffset, seed }) => {
  const radius = {
    x: Math.max(frame.width / 2, 1),
    y: Math.max(frame.height / 2, 1),
  };
  const center = { x: frame.x + radius.x, y: frame.y + radius.y };
  const amplitude = Math.min(
    ELLIPSE_DISTORTION_MAX,
    Math.min(radius.x, radius.y) * ELLIPSE_DISTORTION_RATIO
  );
  const phases = ELLIPSE_FREQUENCIES.map(
    (unused, index) =>
      getMarkerUnitNoise({ index: 90 + index + passOffset, seed }) * Math.PI * 2
  );

  return Array.from({ length: ELLIPSE_SEGMENTS }, (unused, index) =>
    createEllipsePoint({
      amplitude,
      angle: (index / ELLIPSE_SEGMENTS) * Math.PI * 2,
      center,
      phases,
      radius,
    })
  );
};

const getArrowVectors = (points) => {
  const deltaX = points.x2 - points.x1;
  const deltaY = points.y2 - points.y1;
  const length = Math.hypot(deltaX, deltaY);
  return {
    length,
    normalX: length ? -deltaY / length : 0,
    normalY: length ? deltaX / length : 0,
  };
};

/** @returns {{ controlOne: object, controlTwo: object }} curved shaft controls */
export const getRoughArrowControls = ({ passOffset, points, seed }) => {
  const vectors = getArrowVectors(points);
  const bendLimit = Math.min(ARROW_BEND_MAX, vectors.length * ARROW_BEND_RATIO);
  const getBend = (index) =>
    getMarkerJitter({ index: index + passOffset, scale: bendLimit * 2, seed });
  const firstBend = getBend(120);
  const secondBend = getBend(121);

  return {
    controlOne: {
      x: points.x1 + (points.x2 - points.x1) / 3 + vectors.normalX * firstBend,
      y: points.y1 + (points.y2 - points.y1) / 3 + vectors.normalY * firstBend,
    },
    controlTwo: {
      x: points.x1 + ((points.x2 - points.x1) * 2) / 3 + vectors.normalX * secondBend,
      y: points.y1 + ((points.y2 - points.y1) * 2) / 3 + vectors.normalY * secondBend,
    },
  };
};

/** @returns {{x: number, y: number}} visually varied arrowhead wing */
export const getRoughArrowWing = ({ index, passOffset, seed, wing }) => ({
  x:
    wing.x +
    getMarkerJitter({
      index: 130 + index + passOffset,
      scale: WING_DRIFT_SCALE,
      seed,
    }),
  y:
    wing.y +
    getMarkerJitter({
      index: 140 + index + passOffset,
      scale: WING_DRIFT_SCALE,
      seed,
    }),
});
